import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import Container from '../components/Container';
import Button from '../components/Button';

// Hero Section
const Hero = styled.div`
  background: url('https://images.unsplash.com/photo-1542810634-71277d95dcbb?ixlib=rb-1.2.1&auto=format&fit=crop&q=80');
  background-size: cover;
  background-position: center;
  color: ${({ theme }) => theme.colors.light};
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(0,82,156,0.7) 0%, rgba(40,126,60,0.7) 100%);
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 10vh;
    background: linear-gradient(to top, ${({ theme }) => theme.colors.light}, transparent);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 50vh;
  }
`;

const HeroContent = styled.div`
  max-width: 800px;
  padding: 0 ${({ theme }) => theme.spacing.md};
  z-index: 2;
  position: relative;
  
  h1 {
    font-size: ${({ theme }) => theme.fontSizes.huge};
    margin-bottom: ${({ theme }) => theme.spacing.md};
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    text-shadow: 0 2px 8px rgba(0,0,0,0.5);
    color: white;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.xxxl};
    }
  }
  
  p {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    max-width: 600px;
    margin: 0 auto ${({ theme }) => theme.spacing.lg};
    line-height: 1.6;
    text-shadow: 0 2px 4px rgba(0,0,0,0.5);
    color: white;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.md};
    }
  }
`;

const Announcement = styled.div`
  background-color: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.light};
  display: inline-block;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  margin-top: ${({ theme }) => theme.spacing.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  transform: translateY(0);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
  }
`;

// Initiative Section Components
const InitiativeSection = styled(Section) <{ bgColor?: string }>`
  background-color: ${({ bgColor, theme }) =>
    bgColor ? bgColor : theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} 0;
  }
`;

const InitiativeContainer = styled(Container)`
  max-width: 1100px;
`;

const InitiativeCard = styled.div`
  background: ${({ theme }) => theme.colors.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const CardHeader = styled.div<{ bgColor?: string }>`
  background-color: ${({ bgColor, theme }) =>
    bgColor ? bgColor : theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.lg};
  text-align: center;
  position: relative;
  
  h2 {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.xxl};
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: white;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      font-size: ${({ theme }) => theme.fontSizes.xl};
    }
  }
  
  p {
    margin: ${({ theme }) => theme.spacing.sm} 0 0;
    font-size: ${({ theme }) => theme.fontSizes.md};
    opacity: 0.9;
    color: white;
  }
`;

const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const CardText = styled.div`
  p {
    margin-top: 0;
    font-size: ${({ theme }) => theme.fontSizes.md};
    line-height: 1.6;
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const CardActions = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.xl} ${({ theme }) => theme.spacing.xl};
  display: flex;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg};
  }
`;

// Statistics Section
const StatsSection = styled(Section)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  text-align: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} 0;
  }
`;

const StatsContainer = styled(Container)`
  max-width: 1200px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  margin: ${({ theme }) => theme.spacing.xxl} 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: ${({ theme }) => theme.spacing.lg};
  }
`;

const StatCard = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.xl};
  transition: transform 0.3s ease, background-color 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const StatNumber = styled.div`
  font-size: 3.5rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  font-family: ${({ theme }) => theme.fonts.heading};
  color: ${({ theme }) => theme.colors.light};
  line-height: 1;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  opacity: 0.9;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

// Help Details Section
const HelpSection = styled(Section)`
  background-color: ${({ theme }) => theme.colors.background};
  padding: ${({ theme }) => theme.spacing.xxxl} 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.xxl} 0;
  }
`;

const HelpContainer = styled(Container)`
  max-width: 1000px;
`;

const HelpHeading = styled.h2`
  text-align: center;
  margin-bottom: ${({ theme }) => theme.spacing.xxl};
  font-size: ${({ theme }) => theme.fontSizes.xxl};
  color: ${({ theme }) => theme.colors.primary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${({ theme }) => theme.colors.accent};
  }
`;

const HelpList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${({ theme }) => theme.spacing.xl};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const HelpItem = styled.li`
  padding-left: ${({ theme }) => theme.spacing.md};
  position: relative;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.6;
  
  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.accent};
    font-size: 1.2em;
  }
`;

const GratitudeSection = styled(Section)`
  background-color: ${({ theme }) => theme.colors.light};
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  text-align: center;
`;

const GratitudeMessage = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  color: ${({ theme }) => theme.colors.primary};
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  font-style: italic;
  position: relative;
  
  &:before, &:after {
    content: '"';
    font-size: 4rem;
    color: ${({ theme }) => theme.colors.accent};
    opacity: 0.2;
    position: absolute;
    line-height: 0;
  }
  
  &:before {
    left: -20px;
    top: 20px;
  }
  
  &:after {
    right: -20px;
    bottom: 0;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
    
    &:before, &:after {
      font-size: 3rem;
    }
  }
`;

const Latest: React.FC = () => {
  return (
    <>
      {/* Hero Section */}
      <Hero>
        <HeroContent>
          <h1>Latest Initiatives</h1>
          <p>We post our latest initiatives on this page!</p>
          <Announcement>
            Stay tuned for our Ramadan Initiative starting at the end of this month!
          </Announcement>
        </HeroContent>
      </Hero>

      {/* Current Initiatives Section */}
      <InitiativeSection>
        <InitiativeContainer>
          <InitiativeCard>
            <CardHeader bgColor="rgba(0, 122, 61, 0.8)">
              <h2>Gazan News</h2>
              <p>Currently supporting and raising funds</p>
            </CardHeader>
            <CardContent>
              <CardText>
                <p>
                  A family who recently arrived from Gaza has not yet received enough funds for housing and living expenses.
                  We are primarily focused on setting them up for the first 6 months through housing assistance, medical care,
                  employment, transportation and educational support. The goal is to help them become self-sufficient so they
                  can sustain themselves as they settle here.
                </p>
              </CardText>
            </CardContent>
            <CardActions>
              <Link
                to="/donate"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  padding: '0.5rem 1.5rem',
                  fontSize: '1rem',
                  backgroundColor: 'rgba(206, 17, 38, 0.85)',
                  color: 'white',
                  border: '2px solid rgba(206, 17, 38, 0.85)'
                }}
              >
                Donate To Causes Like This
              </Link>
            </CardActions>
          </InitiativeCard>

          <InitiativeCard>
            <CardHeader bgColor="rgba(191, 0, 0, 0.8)">
              <h2>Afghan Families Support</h2>
              <p>Raised Close to $30,000</p>
            </CardHeader>
            <CardContent>
              <CardText>
                <p>
                  Project Assist successfully raised funds to help settle an Afghan Refugee family last year with housing,
                  rent support, transportation, living essentials and continuing education. The family included a young
                  couple with a baby, the husband's sister and elderly mother. They are now well settled and self-sufficient
                  without need for support or further financial assistance.
                </p>
              </CardText>
            </CardContent>
            <CardActions>
              <Link
                to="/donate"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 600,
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  textDecoration: 'none',
                  textAlign: 'center',
                  lineHeight: 1.2,
                  padding: '0.5rem 1.5rem',
                  fontSize: '1rem',
                  backgroundColor: 'transparent',
                  color: 'rgba(0, 122, 61, 0.85)',
                  border: '2px solid rgba(0, 122, 61, 0.85)'
                }}
              >
                Donate To Causes Like This
              </Link>
            </CardActions>
          </InitiativeCard>
        </InitiativeContainer>
      </InitiativeSection>

      {/* Statistics Section */}
      <StatsSection>
        <StatsContainer>
          <h2>Our Impact In Numbers</h2>
          <StatsGrid>
            <StatCard>
              <StatNumber>63</StatNumber>
              <StatLabel>Families Helped</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>$30,467</StatNumber>
              <StatLabel>Funds Raised</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber>2</StatNumber>
              <StatLabel>Months</StatLabel>
            </StatCard>
          </StatsGrid>
        </StatsContainer>
      </StatsSection>

      {/* How We Helped Section */}
      <HelpSection>
        <HelpContainer>
          <HelpHeading>HOW WE HELPED</HelpHeading>
          <HelpList>
            <div>
              <HelpItem>63 families helped</HelpItem>
              <HelpItem>53 out of 63 were zakat eligible individuals / families</HelpItem>
              <HelpItem>10 individuals</HelpItem>
              <HelpItem>15 couples</HelpItem>
              <HelpItem>8 families of 3</HelpItem>
            </div>
            <div>
              <HelpItem>15 families of 4</HelpItem>
              <HelpItem>11 families of 5</HelpItem>
              <HelpItem>2 families with 6 and 2 with 8 family members</HelpItem>
              <HelpItem>Cheques sent to 59 families / individuals</HelpItem>
              <HelpItem>4 visa gift cards</HelpItem>
            </div>
            <div>
              <HelpItem>Additional assistance providing a laptop, clothes and diapers for children and food parcels</HelpItem>
            </div>
          </HelpList>
        </HelpContainer>
      </HelpSection>

      {/* Gratitude Section */}
      <GratitudeSection>
        <Container>
          <GratitudeMessage>
            Thank you to all who supported our efforts to help the community in need
          </GratitudeMessage>
          <div style={{ marginTop: '2rem' }}>
            <Button variant="primary" as={Link} to="/donate">
              Support Our Next Initiative
            </Button>
          </div>
        </Container>
      </GratitudeSection>
    </>
  );
};

export default Latest; 